import {LauncherServiceBase} from '../launcher-service-base';
import {IOrbitConfigsStorageService} from './configs-storage.service.interface';
import {action, computed, observable} from 'mobx';
import {Check} from '@sparkware/app-utils';
import {ILauncherServiceFactory} from '../launcher-service-factory.interface';
import {LAUNCHER_BASE_PATH} from '../routing/launcher-routing-configuration';
import {saveLauncherConfigOverrides} from './save-launcher-config-overrides';
import {IRegulationPresetValues} from '../regulation/regulation-preset-values.interface';
const overrideJson = require('json-override');

export const LOCAL_STORAGE_OVERRIDES_KEYS = {
    IS_IP_WHITELISTED: 'WL.isIPWhiteListed',
    ORBIT_CONFIG_OVERRIDES: 'orbitConfigOverrides'
};


export class ConfigsStorageService extends LauncherServiceBase implements IOrbitConfigsStorageService {

    constructor(services: ILauncherServiceFactory) {
        super(services);
        // load clientData from localStorage
        let clientDataJSONFromUrl = this.services.location.currentUri.queryParams.getParamValue('clientData');
        if(clientDataJSONFromUrl) {
            try {
                this._setConfigurations(JSON.parse(clientDataJSONFromUrl));
                this.services.location.replace(this.services.location.currentUri.getUrlWithoutSearchAndHash());
            } catch (err) {
                this.services.logger.error(`Failed to load launcher configurations from the query params ${clientDataJSONFromUrl}`, err);
                this._loadConfigsFromLocalStorage();
            }


        } else {
            this._loadConfigsFromLocalStorage();
        }


        // load local storage overrides
        this._loadLocalStorageOverrides();
    }

    private _loadConfigsFromLocalStorage() {
        let cgp_client =  this.services.localStorage.getJson('cgp_client') || {};
        this._setConfigurations(cgp_client.clientData);
    }

    @observable
    private _localStorageOverrides: object = {
        [LOCAL_STORAGE_OVERRIDES_KEYS.IS_IP_WHITELISTED]: undefined,
        [LOCAL_STORAGE_OVERRIDES_KEYS.ORBIT_CONFIG_OVERRIDES]: undefined,
    };

    private _isLocalStorageOverrideKey(configurationKey: string): boolean {
        return this._localStorageOverrides.hasOwnProperty(configurationKey);
    }

    @observable
    private _configurations: any = {};

    @computed
    get jsonView(): any {
        return JSON.parse(JSON.stringify(this._configurations));
    }

    @action.bound
    private _setConfigurations(value: any): void {
        this._configurations = value || {};
    }


    @observable.ref
    timeWhenResetWasApplied: number = 0;

    @action.bound
    private _setTimeWhenResetWasApplied() {
        this.timeWhenResetWasApplied = Date.now();
    }

    getValue(configurationKey: string): any {

        if(this._isLocalStorageOverrideKey(configurationKey)) {
            return this._localStorageOverrides[configurationKey];
        }

        let propertyParts = configurationKey.split('.');

        let propertyOwner = this._configurations;
        for(let i = 0; i < propertyParts.length; i++) {
            if(i === propertyParts.length - 1) {
                return propertyOwner[propertyParts[i]];
            } else {
                propertyOwner = propertyOwner[propertyParts[i]]
            }

            if(!Check.isObject(propertyOwner)) {
                return undefined;
            }
        }

        return undefined;
    }

    @action.bound
    updateValue(configurationKey: string, value: any): void {
       if(this._isLocalStorageOverrideKey(configurationKey)) {
           this._localStorageOverrides[configurationKey] = value;
       } else {
           if (Check.isNullOrUndefined(value)) {
               this._deleteValue(configurationKey);
           } else {
               this._setValue(configurationKey, value);
           }
       }
    }

    resetToDefault(configurationKey: string): void {
        this.updateValue(configurationKey, undefined);
    }

    private _deleteValue(configurationKey: string): void {
        let propertyParts = configurationKey.split('.');

        let propertyOwner = this._configurations;
        for(let i = 0; i < propertyParts.length; i++) {
            if(i === propertyParts.length - 1) {
                delete propertyOwner[propertyParts[i]];
            } else {
                propertyOwner = propertyOwner[propertyParts[i]];
                if(!Check.isObject(propertyOwner)) {
                    return;
                }
            }
        }

        this._clearEmptyProps(propertyParts.slice(0, propertyParts.length - 1));
    }

    private _clearEmptyProps(propertyParts: string[]): void {
        if(propertyParts.length === 0) {
            return;
        }

        let propertyAccessorsStack = [];
        let propertyOwner = this._configurations;
        propertyAccessorsStack.push(propertyOwner);
        for(let i = 0; i < propertyParts.length; i++) {
            propertyOwner = propertyOwner[propertyParts[i]];
            propertyAccessorsStack.push(propertyOwner);
        }

        if(Check.isEmpty(propertyOwner)) {
            delete propertyAccessorsStack[propertyAccessorsStack.length - 2][propertyParts[propertyParts.length - 1]]
            this._clearEmptyProps(propertyParts.slice(0, propertyParts.length -1));
        }
    }

    private _setValue(configurationKey: string, value: any): void {
        let propertyParts = configurationKey.split('.');

        let propertyOwner = this._configurations;
        for(let i = 0; i < propertyParts.length; i++) {
            if(i === propertyParts.length - 1) {
                propertyOwner[propertyParts[i]] = value;
            } else {
                if(!propertyOwner[propertyParts[i]]) {
                    propertyOwner[propertyParts[i]] = {};
                }

                propertyOwner = propertyOwner[propertyParts[i]]
            }
        }
    }

    reset(): void {
        this._setConfigurations({});
        this._resetLocalStorageOverrides();
        this._setTimeWhenResetWasApplied();
    }

    @action.bound
    private _resetLocalStorageOverrides() {
        Object.keys(this._localStorageOverrides).forEach(key => {
            this._localStorageOverrides[key] = undefined;
        });
    }

    @action.bound
    private _loadLocalStorageOverrides() {
        Object.keys(this._localStorageOverrides).forEach(key => {
            this._localStorageOverrides[key] = this.services.localStorage.getItem(key);
        });

        if(this._localStorageOverrides[LOCAL_STORAGE_OVERRIDES_KEYS.IS_IP_WHITELISTED] === "true") {
            this._localStorageOverrides[LOCAL_STORAGE_OVERRIDES_KEYS.IS_IP_WHITELISTED] = true;
        }
    }

    save(): void {

        // save clientData
        saveLauncherConfigOverrides(this._configurations, this.services.localStorage);


        // save localStorage overrides
        Object.keys(this._localStorageOverrides).forEach(key => {
            let value = this._localStorageOverrides[key];
            if(Check.isNullOrUndefined(value)) {
                this.services.localStorage.removeItem(key);
            } else {
                this.services.localStorage.setItem(key, value);

            }
        });
    }

    get launcherUrl(): string {
        return this.services.location.currentUri.origin + LAUNCHER_BASE_PATH.toString() + '?clientData=' + JSON.stringify(this._configurations);
    }

    get clientUrl(): string {
        return this.services.location.currentUri.origin + '#clientData=' + JSON.stringify(this._configurations);
    }

    private _createEmptyRegulationPreset(): IRegulationPresetValues {
        return {
            sdk: {
                Skin: {
                    regulationID: null,
                    brandID: null,
                    subBrandID: null,
                },
                Currency: {
                    anonymousCurrency: null
                },
                OAuth2: {
                    fakeClientIP: null
                }
            },
            orbit: {
                country: null,
                language: null,
                iso2: null,
                publicationID: null,
                cmsSource: null
            },
            webData: {
                segments: {
                    IsCIP: null,
                    IsCIS: null
                }
            }
        };
    }

    applyRegulationPreset(presetValues: IRegulationPresetValues) {
        presetValues = overrideJson(this._createEmptyRegulationPreset(), presetValues, true);
        Object.keys(presetValues).forEach(key => {
            this._applyRegulationPresetFieldValue(key, presetValues[key]);
        });
    }

    private _applyRegulationPresetFieldValue(propertyPath: string, propertyValue: any) {
        if(Check.isNullOrUndefined(propertyValue)) {
            this.resetToDefault(propertyPath);
        } else if(Check.isObject(propertyValue)) {
            Object.keys(propertyValue).forEach(propertyName => {
                this._applyRegulationPresetFieldValue(`${propertyPath}.${propertyName}`, propertyValue[propertyName]);
            })
        } else {
            this.updateValue(propertyPath, propertyValue);
        }
    }
}